// pages/Process.js — mobile responsive
// On mobile, the alternating left/right layout stacks vertically

import React, { useEffect } from 'react';
import CtaBand from '../components/CtaBand';

const steps = [
  { num: '01', icon: '💬', title: 'Initial Consultation',      text: "We begin with a conversation. Tell us about your lifestyle, your preferences, and your dreams for your new kitchen.", bullets: ['Free, no-obligation consultation', 'In-home or showroom meeting', 'Discussion of budget and timeline'] },
  { num: '02', icon: '📐', title: 'Site Survey & Measurement',  text: "Our team conducts a comprehensive survey of your space, capturing every dimension and detail. This precision ensures your new kitchen fits perfectly.", bullets: ['Detailed room measurements', 'Assessment of plumbing & electrical', 'Structural considerations'] },
  { num: '03', icon: '🎨', title: 'Design Development',         text: "This is where magic happens. We create detailed 3D renders and technical drawings, refining every element until the design is exactly right.", bullets: ['3D visualisation & renders', 'Material & finish selection', 'Layout optimisation'] },
  { num: '04', icon: '📦', title: 'Material Procurement',       text: "We source only the finest materials from trusted suppliers. Each component is carefully selected to meet our exacting quality standards.", bullets: ['Premium material sourcing', 'Quality assurance checks', 'Sustainable options available'] },
  { num: '05', icon: '🔧', title: 'Expert Installation',        text: "Our skilled installation team brings your design to life with meticulous attention to detail. We manage every aspect of the installation process.", bullets: ['Project management', 'Skilled craftsmanship', 'Daily progress updates'] },
  { num: '06', icon: '✨', title: 'Final Reveal & Aftercare',   text: "The moment you've been waiting for. We conduct a thorough walkthrough, ensuring every detail meets your expectations.", bullets: ['Full walkthrough & handover', 'Aftercare support package', 'Annual maintenance check'] },
];

const timeline = [
  { dur: '2–4 weeks', phase: 'Design Phase',  desc: 'Consultation, measurement, and design development' },
  { dur: '1 week',    phase: 'Approval',       desc: 'Final design sign-off and material confirmation' },
  { dur: '4–8 weeks', phase: 'Manufacturing',  desc: 'Bespoke cabinetry and component production' },
  { dur: '1–2 weeks', phase: 'Installation',   desc: 'Professional fitting and final touches' },
];

export default function Process() {
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  return (
    <div className="page-wrapper--inner">
      <div style={{ height: "60px" }} />
      <style>{`
        .proc-step {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 5rem;
          align-items: center;
          margin-bottom: 5rem;
          padding-bottom: 5rem;
          border-bottom: 1px solid var(--border);
        }
        .proc-step:last-of-type { border-bottom: none; margin-bottom: 0; }
        .tl-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
        }
        @media (max-width: 768px) {
          .proc-step {
            grid-template-columns: 1fr;
            gap: 2rem;
            margin-bottom: 3rem;
            padding-bottom: 3rem;
          }
          /* Hide the decorative visual panel on mobile — just show the text */
          .proc-step .step-vis { display: none; }
          .tl-grid { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 480px) {
          .tl-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <section className="section">
        <div className="container">
          <div className="label">Our Process</div>
          <h2 className="heading-xl">From Vision<br /><span className="orange-italic">to Reality</span></h2>
          <br />
          <p className="subtext" style={{ maxWidth: 620 }}>
            Creating your dream kitchen is a journey. Our refined process ensures every step
            is considered, collaborative, and ultimately rewarding.
          </p>
        </div>
      </section>

      <section className="section section--dark" style={{ paddingTop: 0 }}>
        <div className="container">
          {steps.map((step, i) => (
            <div key={i} className="proc-step">
              {/* Text — always first on mobile (order:1) */}
              <div style={{ order: 1 }}>
                <div style={s.stepNum}>
                  {step.num} <span style={{ fontSize: '1.3rem', color: 'var(--orange)' }}>{step.icon}</span>
                </div>
                <h3 style={s.stepTitle}>{step.title}</h3>
                <p className="body-p">{step.text}</p>
                <ul style={s.bullets}>
                  {step.bullets.map((b, j) => (
                    <li key={j} style={s.bullet}><span style={s.dot} />{b}</li>
                  ))}
                </ul>
              </div>
              {/* Visual — hidden on mobile via CSS above */}
              <div className="step-vis" style={{ ...s.stepVis, order: i % 2 === 0 ? 2 : 0 }}>
                <span style={{ fontSize: '5rem', opacity: 0.25 }}>{step.icon}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="section--sm section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div className="label" style={{ justifyContent: 'center' }}>Timeline</div>
            <h2 style={s.tlH}>What to Expect</h2>
          </div>
          <div className="tl-grid">
            {timeline.map((t, i) => (
              <div key={i} style={s.tlItem}>
                <div style={s.tlDur}>{t.dur}</div>
                <div style={s.tlPhase}>{t.phase}</div>
                <div style={s.tlDesc}>{t.desc}</div>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.8rem', fontStyle: 'normal', marginTop: '2rem' }}>
            *Timelines are indicative and may vary based on project complexity
          </p>
        </div>
      </section>

      <CtaBand
        title="Ready to Begin?"
        text="The first step is a conversation. Let's discuss your vision and explore the possibilities together."
        buttons={[{ label: 'Start Your Journey', to: '/contact' }]}
      />
    </div>
  );
}

const s = {
  stepNum: { fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2.5rem,5vw,4rem)', fontWeight: 400, color: 'rgba(232,130,26,0.3)', lineHeight: 1, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '1rem' },
  stepTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.5rem,3vw,2rem)', fontWeight: 500, marginBottom: '1rem' },
  stepVis: { background: 'var(--bg3)', aspectRatio: '4/3', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  bullets: { listStyle: 'none' },
  bullet: { color: 'var(--text-muted)', fontSize: '0.88rem', padding: '0.3rem 0', display: 'flex', alignItems: 'center', gap: '0.75rem' },
  dot: { width: 6, height: 6, background: 'var(--orange)', borderRadius: '50%', flexShrink: 0 },
  tlH: { fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2rem,3vw,3rem)', fontWeight: 400 },
  tlItem: { padding: '2rem', borderTop: '2px solid var(--orange)' },
  tlDur: { fontFamily: "'Cormorant Garamond', serif", fontSize: '2rem', color: 'var(--orange)', marginBottom: '0.4rem' },
  tlPhase: { fontWeight: 500, fontSize: '0.93rem', marginBottom: '0.6rem' },
  tlDesc: { color: 'var(--text-muted)', fontSize: '0.82rem', lineHeight: 1.6 },
};
